import { useMemo, useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  AlertVariants,
  AlertAppearances,
  Dropdown,
  Field,
  Form,
  TextInput,
  useFormState,
  useFormApi,
} from '@carboledger/ui';
import { useConvertUnitsQuery } from '@/lib/api/unitsApi';
import {
  resolveQuantityColumns,
  buildUnitOptions,
  hasMaterialDensity,
  hasWorkingUnit,
  quantityText,
} from '@/lib/mappers';
import { getDensityUnits } from '@/lib/utils';
import { useDebounce, useTranslations } from '@/hooks';
import { contractValidation } from '../contractsValidation';

// A derived box shows a dash, never a blank or a fabricated 0.
const derivedText = (value) => quantityText(value) || '-';

/**
 * Props:
 *   materials  material view models, for the units and the conversion
 */
export const ContractQuantityFields = ({ materials }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const selectedMaterial = useMemo(
    () => materials.find((material) => material.id === values.materialId) ?? null,
    [materials, values.materialId],
  );

  const convertible = hasWorkingUnit(selectedMaterial);
  const declarationUnit = selectedMaterial?.declarationUnit ?? '';
  const workingUnit = selectedMaterial?.workingUnit ?? '';

  const unitBadges = useMemo(
    () => ({ working: t('UNIT_WORKING'), declaration: t('UNIT_DECLARATION') }),
    [t],
  );

  // Mirrors the server: working unit needs the material's factor; a saved contract keeps its unit.
  const saved = useRef({ materialId: values.materialId, unit: values.unit }).current;
  const keepsSavedUnit = values.materialId === saved.materialId && saved.unit === workingUnit;
  const workingUnusable =
    convertible &&
    workingUnit !== declarationUnit &&
    !hasMaterialDensity(selectedMaterial) &&
    !keepsSavedUnit;

  const unitOptions = useMemo(() => {
    const options = buildUnitOptions(selectedMaterial, unitBadges);
    return workingUnusable ? options.filter((option) => option.value !== workingUnit) : options;
  }, [selectedMaterial, unitBadges, workingUnusable, workingUnit]);

  // A unit the current material doesn't offer can't stay selected.
  useEffect(() => {
    if (values.unit && !unitOptions.some((option) => option.value === values.unit)) {
      setValue('unit', '');
    }
  }, [unitOptions, values.unit, setValue]);

  // First source: the material's correlation; workingUnitStatus covers the saved contract.
  const materialDensity = hasMaterialDensity(selectedMaterial)
    ? String(selectedMaterial.density)
    : null;

  // Guarded so mount doesn't discard a saved contract's density.
  const previousMaterialId = useRef(values.materialId);
  useEffect(() => {
    if (previousMaterialId.current === values.materialId) return;
    previousMaterialId.current = values.materialId;
    setValue('density', materialDensity ?? '');
  }, [values.materialId, materialDensity, setValue]);

  // The material's density owns the field whenever it has one.
  useEffect(() => {
    if (!materialDensity) return;
    if (values.density !== materialDensity) setValue('density', materialDensity);
  }, [materialDensity, values.density, setValue]);

  const density = values.density || null;
  const workingUnitMessage =
    values.workingUnitStatus === 'AVAILABLE' ? null : values.workingUnitMessage;

  const quantity = useDebounce(values.contractedQuantity);

  // Nothing to convert until the quantity, its unit and a density are all known.
  const conversionArgs = useMemo(() => {
    if (!convertible || !quantity || !values.unit || !density) return skipToken;
    return { material: selectedMaterial, unit: values.unit, quantity, density };
  }, [convertible, selectedMaterial, values.unit, quantity, density]);

  const { data: conversion } = useConvertUnitsQuery(conversionArgs);

  const { declarationQuantity, workingQuantity } = useMemo(
    () =>
      resolveQuantityColumns({
        entered: values.contractedQuantity,
        unit: values.unit,
        material: selectedMaterial,
        conversion,
      }),
    [values.contractedQuantity, values.unit, selectedMaterial, conversion],
  );

  const densityUnits = getDensityUnits(declarationUnit, workingUnit);

  return (
    <>
      <Form.Row>
        <Field name="contractedQuantity" required rules={contractValidation.contractedQuantity}>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <TextInput
              id={id}
              name={name}
              label={t('FIELD_CONTRACTED_QUANTITY')}
              placeholder={t('CONTRACTS_FORM_CONTRACTED_QUANTITY_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
            />
          )}
        </Field>

        {/* A contract is only ever entered in the declaration unit. */}
        <Field name="unit" required>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <Dropdown
              id={id}
              name={name}
              label={t('FIELD_UNIT')}
              placeholder={t('FORM_SELECT_UNIT')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
              options={unitOptions}
              emptyMessage={t('FORM_SELECT_MATERIAL_FIRST')}
            />
          )}
        </Field>
      </Form.Row>

      {/* Without a working unit there is nothing to convert, so the whole row goes. */}
      {convertible ? (
        <>
          <Form.Row>
            <TextInput
              disabled
              required
              label={t('CONTRACTS_FORM_CONTRACTED_QUANTITY_DECLARATION_UNIT', {
                unit: declarationUnit,
              })}
              value={derivedText(declarationQuantity)}
            />

            <TextInput
              disabled
              label={t('CONTRACTS_FORM_DENSITY', {
                weightUnit: densityUnits?.weightUnit ?? '',
                volumeUnit: densityUnits?.volumeUnit ?? '',
              })}
              value={density ?? '-'}
            />

            {/* No factor, no conversion — the box could only ever read as a dash. */}
            {density ? (
              <TextInput
                disabled
                label={t('CONTRACTS_FORM_CONTRACTED_QUANTITY_WORKING_UNIT', {
                  unit: workingUnit,
                })}
                value={derivedText(workingQuantity)}
              />
            ) : null}
          </Form.Row>

          {workingUnitMessage ? (
            <Alert variant={AlertVariants.warning} appearance={AlertAppearances.accent}>
              {workingUnitMessage}
            </Alert>
          ) : null}
        </>
      ) : null}
    </>
  );
};
